/* 左边的品牌列表 */
import React, { useRef, useState, useEffect } from 'react'
import get from 'lodash/get'
import classnames from 'classnames'
import buildUrl from 'Pages/auto/home/utils'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import { ContextStore } from './reducer'

import { IConditionsProps, IInfoProps } from './interface'
import applyHttpsImage from 'lib/apply-https-img'
import Link from 'next/link'

interface IProps {
  conditions: IConditionsProps[]
  letters: string[]
  brands: IInfoProps[][]
}

export default function Brand({ letters, brands, conditions }: IProps) {
  const { state, dispatch } = React.useContext(ContextStore)
  // 获取需要监听的dom
  const divRef = useRef<HTMLDivElement>(null)
  // 当前所在的字母
  const [current, setCurrent] = useState('A')
  // 重新定位的位置集合
  const offsetMap = new Map()

  const newOptions = Object.assign({}, state)
  const allConditionKeys = React.useMemo(() => {
    return conditions.map(item => {
      return Array.from(
        new Set(item.data.map(item => item.key).filter(item => !!item))
      )
    })
  }, [conditions])
  useEffect(() => {
    const observer: IntersectionObserver = addObserver()
    divRef.current &&
      [...divRef.current.children].forEach((ele: Element) => {
        observer.observe(ele)
      })
    const brandInfo = get(state, ['brand', 0])
    if (brandInfo && brandInfo.param) {
      // 滚动品牌列表到指定位置
      requestAnimationFrame(() => {
        if (!divRef.current) {
          return
        }
        const target = divRef.current.querySelector(`.brand-${brandInfo.param}`)
        if (!target) {
          return
        }
        // 避免字母遮挡
        divRef.current.scrollTop = (target as HTMLElement).offsetTop - 32
      })
    }
    return () => {
      observer.disconnect()
    }
    // eslint-disable-next-line
  }, [state])

  // 监听品牌位置
  function addObserver(): IntersectionObserver {
    return new IntersectionObserver(
      changes => {
        const lettersTop = divRef.current && divRef.current.offsetTop
        changes.forEach(change => {
          const letter = change.target.getAttribute('data-letter') as string
          const { top } = change.boundingClientRect
          const index = letters.findIndex((item: string) => item === letter)
          if (top <= 0 && !change.isIntersecting) {
            setCurrent(letters[index + 1])
          }
          if (top <= 0 && change.isIntersecting) {
            setCurrent(letters[index])
          }
          if (top === lettersTop && change.isIntersecting) {
            setCurrent(letter)
          }
        })
      },
      {
        root: (divRef.current as unknown) as Element,
        rootMargin: '0px 0px 0px 0px',
        threshold: [0.0000001],
      }
    )
  }

  // 得到每个字母点击要定的位置
  function getOffsetTop() {
    divRef.current &&
      Array.from(divRef.current.children).forEach((item: Element) => {
        offsetMap.set(
          item.getAttribute('data-letter'),
          (item as HTMLElement).offsetTop
        )
      })
  }

  // 点击以后将右边滚动到对应的字母位置
  function scrollView(letter: string) {
    if (offsetMap.size === 0) {
      getOffsetTop()
    }
    if (offsetMap.get(letter) !== undefined) {
      if (divRef.current) {
        divRef.current.scrollTop = offsetMap.get(letter)
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setCurrent(letter)
        })
      })
    }
  }

  function renderLetters() {
    return (
      <ul className="fl">
        {letters.map((letter: string) => (
          <li
            key={letter}
            className={classnames({
              active: letter === current,
            })}
            onClick={(e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
              e.preventDefault()
              e.stopPropagation()
              scrollView(letter)
              sendTeaCommEvent('click_brand_initial_index', {
                initial: letter,
              })
            }}
          >
            {letter}
          </li>
        ))}
        <style jsx>
          {`
            ul {
              width: 32px;
              padding: 11px 0px;
              user-select: none;
              background-color: #1a1a1a;
            }
            li {
              height: 28px;
              line-height: 28px;
              font-size: 16px;
              text-align: center;
              color: #fff;
            }
            li:hover {
              background-color: rgba(255, 255, 255, 0.24);
              transition: background 0.3s ease-in-out;
              cursor: pointer;
            }
            .active {
              color: #1a1a1a;
              transition: all 0.3s ease-in-out;
              background-color: #ffcd32;
            }
            .active:hover {
              background-color: #ffe100;
            }
          `}
        </style>
      </ul>
    )
  }

  function renderBrands() {
    return (
      <div className="brands" ref={divRef}>
        {brands.map((brand: IInfoProps[]) => (
          <div key={brand[0].info.pinyin} data-letter={brand[0].info.pinyin}>
            {brand.map((item: IInfoProps) => {
              if (item.type === '1001') {
                return (
                  <a
                    key={item.info.brand_id}
                    href={`/auto/library/${buildUrl(
                      newOptions,
                      allConditionKeys
                    )}?key=brand&param=${item.info.brand_id}&text=${
                      item.info.brand_name
                    }`}
                  >
                    <p
                      key={item.info.brand_name}
                      className={classnames(
                        'brand',
                        `brand-${item.info.brand_id}`
                      )}
                      onClick={() => {
                        sendTeaCommEvent('click_garage_brand_selector', {
                          brand_id: item.info.brand_id,
                          brand_name: item.info.brand_name,
                        })
                      }}
                    >
                      <img
                        src={applyHttpsImage(item.info.image_url || '')}
                        alt=""
                      />
                      <span>{item.info.brand_name}</span>
                    </p>
                  </a>
                )
              }
              return (
                <h4
                  className="letter"
                  key={item.info.pinyin}
                  data-letter={item.info.pinyin}
                >
                  {item.info.pinyin}
                </h4>
              )
            })}
          </div>
        ))}
        <style jsx>
          {`
            .brands {
              height: 750px;
              overflow-x: hidden;
              overflow-y: auto;
              transform: translateZ(0);
              border: 1px solid #e6e6e6;
              border-left: 0;
            }
            .brand {
              line-height: 52px;
              padding-left: 16px;
              cursor: pointer;
              font-size: 16px;
            }
            .brand:hover {
              color: #406599;
            }
            .brand img {
              width: 36px;
              height: 36px;
              margin-right: 8px;
              border-radius: 50%;
              vertical-align: middle;
            }
            .letter {
              position: sticky;
              top: 0;
              line-height: 32px;
              padding-left: 16px;
              font-size: 12px;
              color: #666;
              background: #f8f8f8;
            }
          `}
        </style>
      </div>
    )
  }

  return (
    <aside className="clearfix">
      {renderLetters()}
      {renderBrands()}
      <style jsx>
        {`
          aside {
            position: sticky;
            top: 0;
            display: inline-block;
            width: 270px;
            height: 666px;
            vertical-align: top;
          }
        `}
      </style>
    </aside>
  )
}
